/**
 * Administration → Storage → Migrations → one migration (1.1.0): its state and actions (Pause, optionally cancelling
 * running transfers; Resume; Cancel; Retry failed; Remove sources after review, with the source's name typed), the
 * counters and bars with rate, ETA and window, the limits and window (editable while it runs), the dry-run report,
 * the files by status (keyset pages, with a CSV of failed and skipped files) and the audit trail.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Ban, Download, Pause, Pencil, Play, RotateCcw, Save, Trash2 } from 'lucide-react';
import { MIGRATION_ITEM_STATUSES, type MigrationDTO, type MigrationItemStatus } from '@prism/contracts';
import { errorDetail } from '../../../api/client';
import {
  isConfirmationRequired, migrationItemsCsvUrl, useMigration, useMigrationAudit, useMigrationItems, useMigrationMutations,
} from '../../../api/hooks';
import { LoadMore } from '../../../components/LoadMore';
import { Modal } from '../../../components/Modal';
import { Segmented } from '../../../components/Segmented';
import { toast } from '../../../components/Toasts';
import { When, bytes, dateTime, rate, relative } from '../../../lib/format';
import { ConfirmDialog, LimitsFields, MigrationBars, PlanReportView, TypedName, countsLabel, limitsDraft, limitsFromDraft, limitsProblem, type LimitsDraft } from './MigrationParts';
import {
  FILE_KIND_LABEL, ITEM_REASON_LABEL, aheadLabel, ITEM_STATUS_LABEL, itemsRefresh, MIGRATION_STATUS_LABEL, REMOVAL_LABEL, startNeedsName, statusLine, statusTone, windowLine, windowOfMigration, windowSummary,
} from './migrationText';

const EDITABLE_LIMITS: MigrationDTO['status'][] = ['draft', 'planned', 'running', 'paused'];
const DELETABLE: MigrationDTO['status'][] = ['draft', 'completed', 'cancelled', 'failed'];
const CANCELLABLE: MigrationDTO['status'][] = ['planning', 'planned', 'running', 'paused', 'copying_done'];

/** Re-renders every 30 s, so "opens Tue 23:00" and relative times stay current between refreshes. */
function useNow(ms = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

export function MigrationDetail({ id, onBack, onEdit }: { id: string; onBack: () => void; onEdit: (m: MigrationDTO) => void }) {
  const q = useMigration(id);
  const m = q.data;
  const w = windowOfMigration(m);
  const now = useNow();
  const muts = useMigrationMutations();
  const [dialog, setDialog] = useState<'pause' | 'cancel' | 'delete' | 'remove' | 'start' | null>(null);
  const [busy, setBusy] = useState(false);

  if (q.error) return <div className="banner error"><div className="banner-body"><strong>Could not load the migration</strong><span>{errorDetail(q.error)}</span><button className="btn sm" onClick={onBack}>Back to migrations</button></div></div>;
  if (!m) return <div className="skeleton" style={{ height: 320 }} />;

  const run = async (fn: () => Promise<unknown>, ok: string, after?: () => void) => {
    setBusy(true);
    try {
      await fn();
      toast(ok);
      setDialog(null);
      after?.();
    } catch (err) {
      toast(errorDetail(err), 'error');
    } finally {
      setBusy(false);
    }
  };
  const c = m.counters;
  const wline = windowLine(m, w, now);
  return (
    <div className="mig-detail" data-testid="mig-detail" data-status={m.status}>
      <div className="mig-detail-head">
        <button className="btn ghost sm" onClick={onBack} data-testid="mig-back-to-list"><ArrowLeft size={14} /> Migrations</button>
      </div>
      <div className="mig-title">
        <div>
          <h2 style={{ margin: 0 }}>{m.name} <span className={`pill ${statusTone(m.status)}`} data-testid="mig-status">{MIGRATION_STATUS_LABEL[m.status]}</span></h2>
          <p className="muted" style={{ margin: '4px 0 0' }}>{m.source.name} → {m.destination.name} · {m.action === 'move' ? `Move, ${REMOVAL_LABEL[m.sourceRemoval].split(':')[0]!.toLowerCase()}${m.sourceRemoval === 'after_days' ? ` (${m.removalAfterDays} days)` : ''}` : 'Copy'}</p>
        </div>
        <div className="mig-actions">
          {(m.status === 'draft' || m.status === 'planned') && <button className="btn" onClick={() => onEdit(m)} data-testid="mig-edit"><Pencil size={14} /> {m.status === 'draft' ? 'Continue' : 'Review'}</button>}
          {m.status === 'planned' && <button className="btn primary" disabled={busy} onClick={() => setDialog('start')} data-testid="mig-detail-start"><Play size={14} /> Start</button>}
          {m.status === 'running' && <button className="btn" disabled={busy} onClick={() => setDialog('pause')} data-testid="mig-pause"><Pause size={14} /> Pause</button>}
          {m.status === 'paused' && <button className="btn primary" disabled={busy} onClick={() => void run(() => muts.resume.mutateAsync({ id }), `Resumed ${m.name}`)} data-testid="mig-resume"><Play size={14} /> Resume</button>}
          {c.failed.files > 0 && m.status !== 'cancelled' && m.status !== 'removing' && (
            <button className="btn" disabled={busy} onClick={() => void run(() => muts.retryFailed.mutateAsync({ id }), `${c.failed.files.toLocaleString('en-US')} failed files queued again`)} data-testid="mig-retry-failed"><RotateCcw size={14} /> Retry failed</button>
          )}
          {m.status === 'copying_done' && <button className="btn danger" disabled={busy} onClick={() => setDialog('remove')} data-testid="mig-remove-sources"><Trash2 size={14} /> Remove sources…</button>}
          {CANCELLABLE.includes(m.status) && <button className="btn ghost danger" disabled={busy} onClick={() => setDialog('cancel')} data-testid="mig-cancel"><Ban size={14} /> Cancel migration</button>}
          {DELETABLE.includes(m.status) && <button className="btn ghost danger" disabled={busy} onClick={() => setDialog('delete')} data-testid="mig-delete"><Trash2 size={14} /> Delete</button>}
        </div>
      </div>

      <section className="panel-card mig-state" data-testid="mig-state">
        <div className="mig-state-line" data-testid="mig-status-line">{statusLine(m, w, now)}</div>
        {wline && <div className="faint" data-testid="mig-window-line">{m.window!.name}: {wline}</div>}
        {m.status === 'copying_done' && (
          <div className="banner warning" data-testid="mig-review-banner"><div className="banner-body">
            <strong>Every file is copied and verified. Review, then remove the sources.</strong>
            <span>{countsLabel(c.done)} are at {m.destination.name} with matching checksums.{c.failed.files ? ` ${countsLabel(c.failed)} failed` : ''}{c.skipped.files ? `${c.failed.files ? ',' : ''} ${countsLabel(c.skipped)} skipped` : ''}{c.failed.files || c.skipped.files ? ': they stay on the source.' : ''}</span>
          </div></div>
        )}
        <MigrationBars m={m} />
        <dl className="kv mig-counters">
          <dt>Planned</dt><dd data-testid="mig-count-total">{countsLabel(c.total)}</dd>
          <dt>{m.action === 'copy' ? 'Copied' : 'Copied and verified'}</dt><dd data-testid="mig-count-done">{countsLabel(c.done)}</dd>
          {m.action === 'move' && <><dt>Sources removed</dt><dd data-testid="mig-count-removed">{countsLabel(c.removed)}</dd></>}
          <dt>Failed</dt><dd data-testid="mig-count-failed" style={c.failed.files ? { color: 'var(--danger)' } : undefined}>{countsLabel(c.failed)}</dd>
          <dt>Skipped</dt><dd data-testid="mig-count-skipped">{countsLabel(c.skipped)}</dd>
          <dt>In flight</dt><dd data-testid="mig-in-flight">{m.inFlight.running} running · {m.inFlight.queued} queued{m.inFlight.waitingAdmission ? ` · ${m.inFlight.waitingAdmission} waiting for a transfer slot` : ''}</dd>
          {(m.limits.maxFilesPerDay != null || m.limits.maxBytesPerDay != null) && <><dt>Today</dt><dd data-testid="mig-today">{m.today.files.toLocaleString('en-US')}{m.limits.maxFilesPerDay != null ? ` of ${m.limits.maxFilesPerDay.toLocaleString('en-US')}` : ''} files · {bytes(m.today.bytes)}{m.limits.maxBytesPerDay != null ? ` of ${bytes(m.limits.maxBytesPerDay)}` : ''}</dd></>}
          <dt>Rate</dt><dd data-testid="mig-rate">{m.bytesPerSecond != null ? rate(m.bytesPerSecond) : '—'}</dd>
          <dt>Finishes</dt><dd data-testid="mig-eta">{m.etaAt ? `${new Date(m.etaAt).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} (${aheadLabel(m.etaAt, now)})` : '—'}</dd>
          <dt>Window</dt><dd>{windowSummary(m.window)}</dd>
          <dt>Started</dt><dd>{m.startedAt ? <>{dateTime(m.startedAt)}{m.startedBy ? ` by ${m.startedBy.displayName}` : ''}</> : '—'}</dd>
          {m.finishedAt && <><dt>Finished</dt><dd>{dateTime(m.finishedAt)}</dd></>}
          {m.notes && <><dt>Notes</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{m.notes}</dd></>}
        </dl>
      </section>

      {EDITABLE_LIMITS.includes(m.status) && <LimitsEditor m={m} />}

      {m.planReport && (
        <details className="panel-card mig-plan-details" open={m.status === 'planned'}>
          <summary className="section-label">The plan (dry run)</summary>
          <PlanReportView report={m.planReport} window={windowOfMigration(m)} />
        </details>
      )}

      {m.status !== 'draft' && <ItemsTable m={m} />}
      <AuditTrail id={id} />

      {dialog === 'pause' && <PauseDialog busy={busy} onClose={() => setDialog(null)} onPause={(cancelRunning) => void run(() => muts.pause.mutateAsync({ id, body: { cancelRunning } }), `Paused ${m.name}`)} />}
      {dialog === 'cancel' && (
        <ConfirmDialog title={`Cancel ${m.name}?`} confirm="Cancel migration" danger busy={busy} testId="mig-confirm-cancel" onClose={() => setDialog(null)}
          onConfirm={() => void run(() => muts.cancel.mutateAsync({ id }), `Cancelled ${m.name}`)}>
          <p className="muted" style={{ margin: 0 }}>Queued and running transfers are cancelled. Files already copied stay at {m.destination.name}, and sources already removed are not brought back. A cancelled migration cannot be resumed.</p>
        </ConfirmDialog>
      )}
      {dialog === 'delete' && (
        <ConfirmDialog title={`Delete ${m.name}?`} confirm="Delete" danger busy={busy} testId="mig-confirm-delete" onClose={() => setDialog(null)}
          onConfirm={() => void run(() => muts.remove.mutateAsync(id), `Deleted ${m.name}`, onBack)}>
          <p className="muted" style={{ margin: 0 }}>The migration and its list of files go; no file moves or is removed. The audit log keeps what it did.</p>
        </ConfirmDialog>
      )}
      {dialog === 'remove' && <ConfirmNameDialog m={m} kind="remove" onClose={() => setDialog(null)} />}
      {dialog === 'start' && <ConfirmNameDialog m={m} kind="start" onClose={() => setDialog(null)} />}
    </div>
  );
}

function PauseDialog({ busy, onPause, onClose }: { busy: boolean; onPause: (cancelRunning: boolean) => void; onClose: () => void }) {
  const [cancelRunning, setCancelRunning] = useState(false);
  return (
    <ConfirmDialog title="Pause the migration?" confirm="Pause" busy={busy} testId="mig-confirm-pause" onClose={onClose} onConfirm={() => onPause(cancelRunning)}>
      <p className="muted" style={{ margin: 0 }}>Nothing new is queued until you resume. Queued transfers that have not started are cancelled and queued again on resume.</p>
      <label className="toggle-row"><input type="checkbox" checked={cancelRunning} onChange={(e) => setCancelRunning(e.target.checked)} data-testid="mig-pause-cancel-running" />
        <span>Also cancel the transfers running now <span className="faint">(they start again from the beginning on resume)</span></span></label>
    </ConfirmDialog>
  );
}

/** Start (a move) and Remove sources, with the source location's name typed; a 428 says it did not match. */
function ConfirmNameDialog({ m, kind, onClose }: { m: MigrationDTO; kind: 'start' | 'remove'; onClose: () => void }) {
  const muts = useMigrationMutations();
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const needsName = kind === 'remove' || startNeedsName(m);
  async function go() {
    setBusy(true);
    setError(null);
    try {
      const body = needsName ? { confirm: typed } : {};
      if (kind === 'start') await muts.start.mutateAsync({ id: m.id, body });
      else await muts.removeSources.mutateAsync({ id: m.id, body });
      toast(kind === 'start' ? `Started ${m.name}` : `Removing the sources of ${m.name}`);
      onClose();
    } catch (err) {
      if (isConfirmationRequired(err)) setError(`Type the source location’s name exactly: ${m.source.name}`);
      else toast(errorDetail(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  const c = m.counters;
  return (
    <Modal title={kind === 'start' ? `Start ${m.name}?` : `Remove the sources of ${m.name}?`} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Back</button>
      <button className={`btn ${kind === 'remove' ? 'danger' : 'primary'}`} disabled={busy || (needsName && typed !== m.source.name)} onClick={() => void go()} data-testid={kind === 'start' ? 'mig-confirm-start' : 'mig-confirm-remove'}>
        {kind === 'start' ? <><Play size={14} /> Start</> : <><Trash2 size={14} /> Remove {c.done.files.toLocaleString('en-US')} source files</>}</button>
    </>}>
      {kind === 'remove' ? (
        <div className="banner warning"><div className="banner-body">
          <strong>{countsLabel(c.done)} copied and verified at {m.destination.name}</strong>
          <span>Before each removal PRISM checks the destination copy’s checksum again, and never removes a file’s last copy. Failed ({c.failed.files.toLocaleString('en-US')}) and skipped ({c.skipped.files.toLocaleString('en-US')}) files stay on {m.source.name}.</span>
        </div></div>
      ) : (
        <p className="muted" style={{ margin: 0 }}>{m.action === 'move' ? `Files move from ${m.source.name} to ${m.destination.name}; sources are removed ${m.sourceRemoval === 'after_review' ? 'only after you review and confirm' : m.sourceRemoval === 'per_file' ? 'as each copy is verified' : `${m.removalAfterDays} days after each copy is verified`}.` : `Files are copied from ${m.source.name} to ${m.destination.name}; nothing is removed.`}</p>
      )}
      {needsName && <TypedName name={m.source.name} value={typed} onChange={(v) => { setTyped(v); setError(null); }} error={error} />}
    </Modal>
  );
}

/** The limits and the window, changed while the migration runs (PATCH: only the limits that changed). */
function LimitsEditor({ m }: { m: MigrationDTO }) {
  const muts = useMigrationMutations();
  const [d, setD] = useState<LimitsDraft>(() => limitsDraft(m.limits));
  const [windowId, setWindowId] = useState<string | null>(m.window?.id ?? null);
  const original = useMemo(() => limitsDraft(m.limits), [m.limits]);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(d) !== JSON.stringify(original) || windowId !== (m.window?.id ?? null);
  const problem = limitsProblem(d);
  async function save() {
    setBusy(true);
    try {
      const next = limitsFromDraft(d);
      const before = limitsFromDraft(original);
      const patch = Object.fromEntries(Object.entries(next).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(before[k as keyof typeof before])));
      await muts.update.mutateAsync({ id: m.id, body: { ...(Object.keys(patch).length ? { limits: patch } : {}), ...(windowId !== (m.window?.id ?? null) ? { windowId } : {}) } });
      toast('Limits saved; they apply from the next round');
    } catch (err) {
      toast(errorDetail(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  return (
    <details className="panel-card mig-limits-card" data-testid="mig-limits-editor" open={m.status === 'running' || m.status === 'paused' ? undefined : true}>
      <summary className="section-label">Window and limits{m.status === 'running' || m.status === 'paused' ? ' (change them while it runs)' : ''}</summary>
      <LimitsFields value={d} onChange={setD} windowId={windowId} onWindow={setWindowId} idPrefix="mig-edit" />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn primary sm" disabled={!dirty || Boolean(problem) || busy} onClick={() => void save()} data-testid="mig-save-limits"><Save size={13} /> Save</button>
        {dirty && <button className="btn ghost sm" onClick={() => { setD(original); setWindowId(m.window?.id ?? null); }}>Undo</button>}
        {problem && <span className="error-text">{problem}</span>}
      </div>
    </details>
  );
}

const ITEM_FILTERS: Array<MigrationItemStatus | 'all'> = ['all', ...MIGRATION_ITEM_STATUSES];

function ItemsTable({ m }: { m: MigrationDTO }) {
  const [status, setStatus] = useState<MigrationItemStatus | 'all'>(m.counters.failed.files ? 'failed' : 'all');
  const running = m.status === 'running' || m.status === 'removing';
  const items = useMigrationItems(m.id, status, running);
  const rows = items.data?.items ?? [];
  // One page loaded: the files follow the migration by themselves (every 10 s, and on a new status or counts). After
  // Load more a refetch would reload every page, so the list waits for the Refresh button instead.
  const refreshMode = itemsRefresh(running, items.pageCount);
  const { refetch } = items;
  const progress = `${m.status}:${m.counters.done.files}:${m.counters.failed.files}:${m.counters.skipped.files}:${m.counters.removed.files}`;
  const followProgress = items.pageCount <= 1;
  useEffect(() => { if (followProgress) void refetch(); }, [progress, refetch]); // eslint-disable-line react-hooks/exhaustive-deps -- not on Load more itself
  return (
    <section className="panel-card" data-testid="mig-items">
      <div className="mig-items-head">
        <p className="section-label" style={{ margin: 0 }}>Files</p>
        <span style={{ flex: 1 }} />
        {refreshMode === 'manual' && (
          <span className="mig-items-refresh" data-testid="mig-items-refresh">
            <span className="faint">Updated {new Date(items.dataUpdatedAt).toLocaleTimeString('en-GB', { hour12: false })}</span>
            <button type="button" className="btn ghost sm" disabled={items.isFetching} onClick={() => void refetch()} data-testid="mig-items-refresh-button"><RotateCcw size={13} /> Refresh</button>
          </span>
        )}
        <a className="btn ghost sm" href={migrationItemsCsvUrl(m.id)} download data-testid="mig-items-csv"><Download size={13} /> Failed and skipped (CSV)</a>
      </div>
      <Segmented value={status} onChange={setStatus} label="Files by status" options={ITEM_FILTERS.map((s) => ({ value: s, label: s === 'all' ? 'All' : ITEM_STATUS_LABEL[s], testId: `mig-items-${s}` }))} />
      <div className="mig-table-wrap">
        <table className="table compact mig-table">
          <thead><tr><th>Asset</th><th>File</th><th>Size</th><th>Status</th><th>Reason</th><th>Tries</th><th>When</th></tr></thead>
          <tbody>
            {rows.map((i) => (
              <tr key={i.id} data-testid="mig-item-row" data-status={i.status}>
                <td><Link to={`/assets/${i.assetId}`}>{i.assetTitle ?? 'Asset'}</Link></td>
                <td title={i.objectKey}><span className="faint">{FILE_KIND_LABEL[i.fileKind]}</span> <span className="mono">{i.objectKey.split('/').pop()}</span></td>
                <td className="num">{bytes(i.sizeBytes)}</td>
                <td>{ITEM_STATUS_LABEL[i.status]}</td>
                <td className="mig-reason" title={i.error ?? undefined}>{i.reason ? ITEM_REASON_LABEL[i.reason] : ''}{i.error ? <span className="faint"> · {i.error}</span> : ''}{i.nextAttemptAt && i.status === 'pending' ? <span className="faint"> · next try {relative(i.nextAttemptAt)}</span> : ''}</td>
                <td className="num">{i.attempts || ''}</td>
                <When as="td" className="muted" iso={i.removedAt ?? i.finishedAt ?? i.verifiedAt ?? i.queuedAt} />
              </tr>
            ))}
            {!items.isLoading && rows.length === 0 && <tr><td colSpan={7} className="muted">No files {status === 'all' ? 'yet' : `with this status`}.</td></tr>}
          </tbody>
        </table>
      </div>
      <LoadMore list={items} shown={rows.length} noun="files" />
    </section>
  );
}

const AUDIT_LABEL: Record<string, string> = {
  'storage.migration.create': 'Created', 'storage.migration.update': 'Changed', 'storage.migration.plan': 'Planned',
  'storage.migration.start': 'Started', 'storage.migration.pause': 'Paused', 'storage.migration.resume': 'Resumed',
  'storage.migration.cancel': 'Cancelled', 'storage.migration.retry_failed': 'Retried failed files',
  'storage.migration.remove_sources': 'Removed the sources', 'storage.migration.complete': 'Completed', 'storage.migration.delete': 'Deleted',
};

function AuditTrail({ id }: { id: string }) {
  const q = useMigrationAudit(id);
  const items = q.data?.items ?? [];
  if (!items.length) return null;
  return (
    <section className="panel-card" data-testid="mig-audit">
      <p className="section-label">Audit trail</p>
      <ol className="mig-audit">
        {items.map((a) => (
          <li key={a.id}><span>{AUDIT_LABEL[a.action] ?? a.action}</span><span className="faint">{a.actor?.displayName ?? 'PRISM'} · {dateTime(a.at)}</span></li>
        ))}
      </ol>
    </section>
  );
}
